import { useMemo, useState } from 'react';
import { IconDownload, IconTrash } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { Button, useToast } from '@hana/ui';
import { norm } from '../finance/view';
import { RCP_NO_FILTER, rcpFilter, rcpLinked, rcpOpen, rcpPeriod, rcpUsd, type RcpFilter } from './rcp';
import { RcpModal } from './RcpModal';
import { useRcpRows } from './useArSource';
import './receipts.i18n';
import s from './ar.module.css';

// Mint-AR › tab "Phiếu thu" (1536). TIỀN → chỉ khi có quyền `money`; chỉ đọc: Duyệt / Bỏ duyệt đổi trạng thái tại chỗ, ghi thật báo toast.
const nf = (x: number): string => x.toLocaleString('vi-VN', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const FLT: readonly (readonly [keyof RcpFilter, string, readonly (readonly [string, string])[]])[] = [
  ['per', 'rc.fPer', [['all', 'rc.all'], ['10', 'rc.m10'], ['09', 'rc.m09']]],
  ['acc', 'rc.fAcc', [['all', 'rc.all'], ['VND-TPBANK-CTY', ''], ['VND-Techcombank-CTY', ''], ['USD-TPBANK-CTY', '']]],
  ['type', 'rc.fType', [['all', 'rc.all'], ['inv', 'rc.byInv'], ['cash', 'rc.cash']]],
  ['br', 'rc.fBr', [['all', 'rc.all'], ['SGN', ''], ['HAN', ''], ['HPH', '']]],
  ['min', 'rc.fMin', [['', 'rc.anyAmt'], ['50', ''], ['100', ''], ['200', '']]],
];
const HEAD: readonly (readonly [string, boolean])[] = [['rc.hNo', false], ['rc.hCus', false], ['rc.hAcc', false], ['rc.hAmt', true], ['rc.hLink', false], ['rc.hBy', false], ['rc.hAp', false]];
type Seg = 'all' | 'pend' | 'ok';

export function ReceiptsTable({ money, q }: { money: boolean; q: string }) {
  const t = useT();
  const toast = useToast();
  const [seg, setSeg] = useState<Seg>('all');
  const [f, setF] = useState<RcpFilter>(RCP_NO_FILTER);
  const [open_, setOpen] = useState<string | null>(null);
  const [ap, setAp] = useState<Readonly<Record<string, boolean>>>({});
  const src = useRcpRows();
  const rows = useMemo(() => src.map((r) => ({ ...r, ap: ap[r.no] ?? r.ap })), [src, ap]);
  const m = (x: number) => (money ? nf(x) : '•••••');
  const base = rcpFilter(rows, { ...f, q }, norm);
  const list = base.filter((r) => seg === 'all' || (seg === 'pend' ? !r.ap : r.ap));
  const pend = rows.filter((r) => !r.ap);
  const m10 = rows.filter((r) => rcpPeriod(r) === '10');
  const vnd = m10.filter((r) => r.cur === 'VND');
  const open = rows.filter(rcpOpen);
  const hasF = JSON.stringify({ ...f, q: '' }) !== JSON.stringify(RCP_NO_FILTER);
  const set = (p: Partial<RcpFilter>) => setF({ ...f, ...p });
  const toggle = (no: string, v: boolean) => {
    if (v && rows.find((r) => r.no === no)?.by === 'DAVID') { toast({ msg: t('rc.selfAp', { no }) }); return; }
    setAp({ ...ap, [no]: v });
    toast({ msg: t(v ? 'rc.apDone' : 'rc.unapDone', { no }) });
  };
  const SEGS: readonly (readonly [Seg, string, number])[] = [['all', t('rc.all'), base.length], ['pend', t('rc.pend'), base.filter((r) => !r.ap).length], ['ok', t('rc.ok'), base.filter((r) => r.ap).length]];
  const KPI = [
    { k: t('rc.kVnd'), v: money ? t('rc.tr', { n: nf(vnd.reduce((a, r) => a + r.amt, 0)) }) : '•••••', sub: t('rc.vndSub', { n: vnd.length }), on: false, go: () => set({ per: '10' }) },
    { k: t('rc.kUsd'), v: money ? t('rc.usd', { n: m10.reduce((a, r) => a + rcpUsd(r), 0).toLocaleString('vi-VN') }) : '•••••', sub: t('rc.usdSub', { n: m10.filter((r) => rcpUsd(r) > 0).length }), on: false, go: () => set({ per: '10', acc: 'USD-TPBANK-CTY' }) },
    { k: t('rc.kOpen'), v: String(open.length), sub: money ? t('rc.openSub', { n: nf(open.reduce((a, r) => a + r.amt - rcpLinked(r), 0)) }) : '', on: f.openOnly, go: () => set({ openOnly: !f.openOnly }), c: open.length ? s.warn : s.ok },
    { k: t('rc.pend'), v: String(pend.length), sub: t('rc.pendSub'), on: seg === 'pend', go: () => setSeg(seg === 'pend' ? 'all' : 'pend'), c: pend.length ? s.warn : s.ok },
  ];

  return (
    <section className={s.card} aria-label={t('rc.title')}>
      <div className={s.rcBar}>
        <div className={s.seg} role="group" aria-label={t('rc.apAria')}>
          {SEGS.map(([k, l, n]) => <button key={k} type="button" aria-pressed={seg === k} onClick={() => setSeg(k)}>{l} <span>{n}</span></button>)}
        </div>
        <span className={`${s.lb} ${s.grow}`}>{t('rc.count', { n: list.length, total: rows.length, sum: m(list.reduce((a, r) => a + r.amt, 0)) })}</span>
        {pend.length > 0 && <Button variant="primary" size="sm" onClick={() => { const n = { ...ap }; pend.filter((r) => r.by !== 'DAVID').forEach((r) => { n[r.no] = true; }); setAp(n); toast({ msg: t('rc.apAll', { n: pend.length }) }); }}>{t('rc.apBtn', { n: pend.length })}</Button>}
        <Button size="sm" icon={<IconDownload size={16} aria-hidden="true" />} onClick={() => toast({ msg: t('rc.xls', { n: list.length }) })}>{t('rc.xlsBtn')}</Button>
      </div>
      <div className={s.kpi4}>
        {KPI.map((k) => (
          <button key={k.k} type="button" className={k.on ? s.kOn : ''} onClick={k.go}><span className={s.lb}>{k.k}</span><b className={k.c ?? ''}>{k.v}</b><span className={s.lb}>{k.sub}</span></button>
        ))}
      </div>
      <div className={s.flt} role="group" aria-label={t('rc.fltAria')}>
        {FLT.map(([key, label, opts]) => (
          <label key={key}>{t(label)}
            <select aria-label={t(label)} value={String(f[key])} onChange={(e) => set({ [key]: e.target.value })}>{opts.map(([v, l]) => <option key={v} value={v}>{l ? t(l) : (key === 'min' ? `≥ ${t('rc.tr', { n: v })}` : v)}</option>)}</select>
          </label>
        ))}
        <label className={s.chk}><input type="checkbox" checked={f.openOnly} onChange={(e) => set({ openOnly: e.target.checked })} />{t('rc.openOnly')}</label>
        {hasF && <button type="button" className={s.clr} onClick={() => setF(RCP_NO_FILTER)}><IconTrash size={16} aria-hidden="true" />{t('rc.clear')}</button>}
      </div>
      <table className={`${s.tbl} ${s.rcp}`}>
        <thead><tr>{HEAD.map(([l, right]) => <th key={l} className={right ? s.r : ''}>{t(l)}</th>)}<th /></tr></thead>
        <tbody>
          {list.map((r) => (
            <tr key={r.no} title={t('rc.rowTip')} className={s.clk} onClick={() => setOpen(r.no)}>
              <td><b className={s.dno}>{r.no}</b><div className={s.sale}>{r.date}</div></td>
              <td><b className={s.dno2}>{r.cus}</b><div className={`${s.sale} ${s.job}`}>{r.memo}</div></td>
              <td className={s.tx2}>{r.bank}<div className={s.sale}>{t(r.type === 'cash' ? 'rc.cash' : 'rc.byInv')} · {r.branch}</div></td>
              <td className={s.r}><b>{m(r.amt)}</b><div className={s.sale}>{r.cur}</div></td>
              <td className={r.links.length ? s.tx2 : s.warnTx}>{r.links.length ? r.links.map((l) => l[0]).join(', ') : t('rc.prepaid')}</td>
              <td className={s.tx2}>{r.by}</td>
              <td>
                <span className={`${s.flag} ${r.ap ? s.fOk : s.fWarn}`}><i />{t(r.ap ? 'rc.ok' : 'rc.pend')}</span>
                <div className={s.sale}>{r.ap ? t('rc.apBy', { by: r.apBy || 'LIENNTK' }) : rcpOpen(r) ? t('rc.left', { n: m(r.amt - rcpLinked(r)) }) : ''}</div>
              </td>
              <td className={s.r}><button type="button" className={r.ap ? s.act2 : s.act3} onClick={(e) => { e.stopPropagation(); toggle(r.no, !r.ap); }}>{t(r.ap ? 'rc.unap' : 'rc.hAp')}</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className={s.foot}><span>{t('rc.foot')}</span></div>
      <RcpModal row={rows.find((r) => r.no === open_) ?? null} money={money} me="DAVID" onClose={() => setOpen(null)} onApprove={toggle} />
    </section>
  );
}
